import { useEffect, useRef, useCallback } from 'react';
import { UserData, BorrowerData, Ownership, FormValues } from '../../../types';
import { useBorrower } from '../context/BorrowerContext';
import { useOwnership } from '../../../features/onboarding/context/OwnershipContext';

interface UseAutoContinuePreApprovalParams {
  showPreApprovalModal: boolean;
  values: FormValues;
  isProcessingTeamMode: boolean;
  onContinue: (userData: UserData) => void;
}

interface BuildUserDataParams {
  investmentAddress: string;
  borrowerData: BorrowerData;
  ownershipSelected: Ownership | null;
  ownershipsFiltered: Ownership[];
  values: FormValues;
}

/**
 * Builds normalized user data for auto-continue flow
 * Similar to PreApprovalForm's buildNormalizedUserData function
 */
const buildNormalizedUserData = ({
  investmentAddress,
  borrowerData,
  ownershipSelected,
  ownershipsFiltered,
  values,
}: BuildUserDataParams): UserData => {
  const ownershipFallback = ownershipSelected || ownershipsFiltered?.[0];
  const isSomeoneElse = ownershipSelected?.isNew === true;
  
  const borrowerNameString = typeof borrowerData?.name === 'string' ? borrowerData.name.trim() : '';
  const borrowerNameParts = borrowerNameString.length > 0 ? borrowerNameString.split(' ') : [];
  const borrowerFirstName = borrowerNameParts[0] || '';
  const borrowerLastName = borrowerData?.lastName?.trim() || borrowerNameParts.slice(1).join(' ') || '';
  const borrowerPhone = typeof borrowerData?.phone === 'string' ? borrowerData.phone.trim() : '';

  // If "Someone else" is selected, use guarantor names from values
  const guarantorFirstName = isSomeoneElse ? (values.guarantorFirstName as string) : '';
  const guarantorLastName = isSomeoneElse ? (values.guarantorLastName as string) : '';

  // Priority: guarantor names (if someone else) > ownership name > borrower name
  const normalizedFirstName =
    (typeof guarantorFirstName === 'string' && guarantorFirstName.trim()) ||
    (typeof ownershipFallback?.name === 'string' && ownershipFallback.name.trim() && ownershipFallback.name !== 'Someone' ? ownershipFallback.name.trim() : '') ||
    borrowerFirstName;

  const normalizedLastName =
    (typeof guarantorLastName === 'string' && guarantorLastName.trim()) ||
    (typeof ownershipFallback?.lastname === 'string' && ownershipFallback.lastname.trim() && ownershipFallback.lastname !== 'else' ? ownershipFallback.lastname.trim() : '') ||
    borrowerLastName;

  const normalizedPhone =
    (isSomeoneElse ? borrowerPhone : '') ||
    (typeof ownershipFallback?.phone === 'string' ? ownershipFallback.phone.trim() : '') ||
    borrowerPhone;

  return {
    firstName: normalizedFirstName,
    lastName: normalizedLastName,
    email: ownershipFallback?.email || borrowerData?.email || '',
    phoneNumber: normalizedPhone,
    password: '', // Empty for authenticated users
    passwordConfirmation: '', // Empty for authenticated users
    investmentAddress: investmentAddress.trim(),
  } as any;
};

/**
 * Custom hook to handle auto-continue logic for PreApprovalForm
 * If user has an investment address and is authenticated, automatically continues
 * without showing the modal
 * 
 * @returns shouldAutoContinue - function to check if auto-continue conditions are met
 */
export const useAutoContinuePreApproval = ({
  showPreApprovalModal,
  values,
  isProcessingTeamMode,
  onContinue,
}: UseAutoContinuePreApprovalParams): (() => boolean) => {
  const { borrowerData, ownershipsFiltered } = useBorrower();
  const { ownershipSelected } = useOwnership();
  const autoContinueExecutedRef = useRef(false);

  /**
   * Checks if auto-continue conditions are met and builds userData if so
   */
  const buildAutoUserData = useCallback((): UserData | null => {
    const investmentAddress = values['Investment property address'];

    // Check if address exists and is valid
    if (!investmentAddress || typeof investmentAddress !== 'string' || investmentAddress.trim() === '') {
      return null;
    }

    // Only auto-continue if user is authenticated (has borrowerData)
    if (!borrowerData) {
      return null;
    }

    // Build userData using normalized data
    return buildNormalizedUserData({
      investmentAddress,
      borrowerData,
      ownershipSelected: ownershipSelected || null,
      ownershipsFiltered: ownershipsFiltered || [],
      values,
    });
  }, [values, borrowerData, ownershipSelected, ownershipsFiltered]);

  /**
   * Checks if auto-continue conditions are met (without building userData)
   * Used to prevent modal from opening
   */
  const shouldAutoContinue = useCallback((): boolean => {
    if (isProcessingTeamMode) {
      return false;
    }

    const investmentAddress = values['Investment property address'];

    // Check if address exists and is valid
    if (!investmentAddress || typeof investmentAddress !== 'string' || investmentAddress.trim() === '') {
      return false;
    }

    // Only auto-continue if user is authenticated (has borrowerData)
    if (!borrowerData) {
      return false;
    }

    return true;
  }, [values, borrowerData, isProcessingTeamMode]);

  /**
   * Auto-continue effect: executes onContinue automatically if conditions are met
   */
  useEffect(() => {
    if (showPreApprovalModal && !isProcessingTeamMode && !autoContinueExecutedRef.current) {
      const autoUserData = buildAutoUserData();
      if (autoUserData) {
        // User has address, auto-continue without showing modal
        autoContinueExecutedRef.current = true;
        onContinue(autoUserData);
      }
    } else if (!showPreApprovalModal) {
      // Reset ref when modal closes
      autoContinueExecutedRef.current = false;
    }
  }, [showPreApprovalModal, buildAutoUserData, isProcessingTeamMode, onContinue]);

  return shouldAutoContinue;
};

